/* eslint-disable @typescript-eslint/no-unused-vars */
import { Col, DatePicker, Form, FormInstance, Row, Typography } from "antd";
import CompanyCard from "containers/Admin/CreateOrEditEvent/Cards/CompanyCard";
import dayjs, { Dayjs } from "dayjs";
import { getDateComponentsNorwegian, momentToNorwegianISO } from "helpers/dateUtils";
import { EditEventFormType } from "../CreateOrEditEvent";

const { Text } = Typography;

interface Props {
  form: FormInstance<EditEventFormType>;
}

const CompanyCardTime: React.FC<Props> = ({ form }) => {
  const adjustMomentWithoutSeconds = (value: Dayjs | null) => (value ? dayjs(value).second(0) : null);

  const formatString = "YYYY-MM-DDTHH:mm:ssZ";

  const onChangeStartTime = (value: Dayjs | null) => {
    const adjustedValue = adjustMomentWithoutSeconds(value);
    if (adjustedValue) {
      // Convert to Norwegian timezone ISO string
      const norwegianISO = momentToNorwegianISO(adjustedValue);
      if (norwegianISO) {
        form.setFieldsValue({ start: norwegianISO });

        // Calculate close and open times based on Norwegian timezone
        const closeMoment = dayjs(adjustedValue).add(-1, "day");
        const openMoment = dayjs(adjustedValue).hour(12).minute(0).add(-5, "day");
        const closeISO = momentToNorwegianISO(closeMoment);
        const openISO = momentToNorwegianISO(openMoment);
        if (closeISO) form.setFieldsValue({ close: closeISO });
        if (openISO) form.setFieldsValue({ open: openISO });
      }
    }
  };

  const onChangeOpenTime = (value: Dayjs | null) => {
    const adjustedValue = adjustMomentWithoutSeconds(value);
    if (adjustedValue) {
      const norwegianISO = momentToNorwegianISO(adjustedValue);
      if (norwegianISO) {
        form.setFieldsValue({ open: norwegianISO });
      }
    }
  };

  const onChangeCloseTime = (value: Dayjs | null) => {
    const adjustedValue = adjustMomentWithoutSeconds(value);
    if (adjustedValue) {
      const norwegianISO = momentToNorwegianISO(adjustedValue);
      if (norwegianISO) {
        form.setFieldsValue({ close: norwegianISO });
      }
    }
  };

  const getValueProps = (value: string | undefined) => {
    if (!value) return { value: null };

    // Parse the date in Norwegian timezone to get correct components
    const components = getDateComponentsNorwegian(value);
    if (!components) return { value: null };

    // Create a dayjs object from the Norwegian timezone components
    // This ensures the DatePicker displays the time in Norwegian timezone
    const norwegianMoment = dayjs()
      .year(components.year)
      .month(components.month)
      .date(components.day)
      .hour(components.hours)
      .minute(components.minutes)
      .second(0);

    return { value: norwegianMoment };
  };

  return (
    <>
      <CompanyCard title="Tider">
        <Row gutter={[10, 5]}>
          <Col>
            <Form.Item<EditEventFormType>
              name="start"
              label="Arrangementstidspunkt"
              getValueProps={getValueProps}
              rules={[{ required: true, message: "Velg tidspunkt for arrangementet" }]}
            >
              <DatePicker
                size="large"
                showTime
                allowClear={false}
                onChange={onChangeStartTime}
                format="DD.MM.YYYY HH:mm"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Text italic style={{ marginBottom: "20px" }}>
            Under velges tidspunkt for når arrangementet automatisk åpner for påmelding, og når avmelding stenges. Dette
            settes automatisk ut ifra arrangementstidspunkt, og er som regel ikke nødvendig å endre.
          </Text>
          <Col md={12} sm={24} xs={24} style={{ marginBottom: "10px" }}>
            <Form.Item<EditEventFormType>
              name="open"
              label="Åpning"
              getValueProps={getValueProps}
              dependencies={["close"]}
              rules={[
                { required: true, message: "Velg tidspunkt for åpning av påmelding" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const closeValue = getFieldValue("close");
                    if (!value || !closeValue) {
                      return Promise.resolve();
                    }
                    const openMoment = dayjs(value);
                    const closeMoment = dayjs(closeValue);
                    if (openMoment.isBefore(closeMoment)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Åpningstidspunkt må være før stengningstidspunkt"));
                  },
                }),
              ]}
            >
              <DatePicker
                size="large"
                showTime
                allowClear={false}
                onChange={onChangeOpenTime}
                format="DD.MM.YYYY HH:mm"
              />
            </Form.Item>
          </Col>
          <Col md={12} sm={24} xs={24}>
            <Form.Item<EditEventFormType>
              name="close"
              label="Stengning"
              getValueProps={getValueProps}
              dependencies={["open"]}
              rules={[
                { required: true, message: "Velg tidspunkt for stengning av avmelding" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const openValue = getFieldValue("open");
                    if (!value || !openValue) {
                      return Promise.resolve();
                    }
                    const closeMoment = dayjs(value);
                    const openMoment = dayjs(openValue);
                    if (closeMoment.isAfter(openMoment)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Stengningstidspunkt må være etter åpningstidspunkt"));
                  },
                }),
              ]}
            >
              <DatePicker
                size="large"
                showTime
                allowClear={false}
                onChange={onChangeCloseTime}
                format="DD.MM.YYYY HH:mm"
              />
            </Form.Item>
          </Col>
        </Row>
      </CompanyCard>
    </>
  );
};

export default CompanyCardTime;
